import { formatDistance, tr } from '@app/i18n'
import { dropWarmup, warmupAr } from '../ar/permissions'
import { importChunk } from '../lazy-retry'
import { arrowAngleDeg, type Turn, turnFromBoard } from '../nav/orientation'
import { bindArrow, confirmGo, exitOrient, type OrientContext, orientContext } from '../orient'
import {
  type GuidancePref,
  guidancePref,
  langTick,
  orientHeading,
  orientSensors,
  orientStale,
  routePlan,
  setGuidancePref,
  targetId,
  venue,
} from '../state'
import { Glyph } from './glyph'
import { GLYPH_AR, GLYPH_MAP } from './glyphs'
import { featureName } from './labels'

const STATIC_ANGLE: Readonly<Record<Turn, number>> = {
  straight: 0,
  right: 90,
  back: 180,
  left: 270,
}

/** Стороны света на кольце компаса (север — амбером, он якорь взгляда). */
const CARDINALS = ['orient.north', 'orient.east', 'orient.south', 'orient.west'] as const

/**
 * Компас-диск 260×260 (ScreenOrient прототипа): светящийся диск, кольца,
 * стороны света и «скульптурная» стрелка path-градиента. Стрелка живая после
 * привязки к компасу, статичная — по фразе поворота от щита.
 */
function Compass({ angleDeg, live }: { angleDeg: number | null; live: boolean }) {
  if (angleDeg === null) return null
  return (
    <div class="orient-compass">
      <div class="orient-compass-glow" aria-hidden="true" />
      <svg viewBox="0 0 200 200" aria-hidden="true">
        <defs>
          <linearGradient id="orient-arrow" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stop-color="var(--path)" />
            <stop offset="1" stop-color="var(--path-2)" />
          </linearGradient>
        </defs>
        <circle cx="100" cy="100" r="94" fill="none" stroke="var(--line)" stroke-width="1" />
        <circle
          cx="100"
          cy="100"
          r="80"
          fill="none"
          stroke="var(--path)"
          stroke-opacity="0.3"
          stroke-width="1"
          stroke-dasharray="2 6"
        />
        {CARDINALS.map((key, i) => (
          <text
            key={key}
            x={100 + 72 * Math.sin((i * Math.PI) / 2)}
            y={100 - 72 * Math.cos((i * Math.PI) / 2) + 4}
            text-anchor="middle"
            font-size="11"
            font-weight="800"
            fill={i === 0 ? 'var(--amber)' : 'var(--ink-3)'}
          >
            {tr(key)}
          </text>
        ))}
        <g transform={`rotate(${Math.round(angleDeg)} 100 100)`}>
          <path
            d="M100 22 L122 108 L100 96 L78 108 Z"
            fill="url(#orient-arrow)"
            stroke="var(--card)"
            stroke-width="1.5"
            stroke-linejoin="round"
            opacity={live ? 1 : 0.75}
          />
          <path d="M100 22 L122 108 L100 96 Z" fill="var(--card)" opacity="0.25" />
        </g>
        <circle cx="100" cy="100" r="7" fill="var(--ink)" />
      </svg>
    </div>
  )
}

/** Живая стрелка — от привязанного взгляда; статичная — сектор фразы L0. */
function resolveAngle(ctx: OrientContext, world: number | null): number | null {
  if (ctx.targetBearingDeg === null) return null
  if (world !== null) return arrowAngleDeg(ctx.targetBearingDeg, world)
  if (ctx.phraseKey && ctx.anchorHeadingDeg !== null) {
    return STATIC_ANGLE[turnFromBoard(ctx.targetBearingDeg, ctx.anchorHeadingDeg)]
  }
  return null
}

/**
 * Бейдж честности (ScreenOrient прототипа, три уровня): чем мы на самом деле
 * знаем направление. Никогда не обещаем точность, которой нет.
 */
function AccuracyBadge({ bound }: { bound: boolean }) {
  const sensors = orientSensors.value
  const blind = sensors === 'denied' || sensors === 'unsupported'
  const precise = bound && !orientStale.value
  const cls = precise ? 'pill pill-path' : blind ? 'pill pill-quiet' : 'pill pill-amber'
  const key = precise ? 'orient.acc_precise' : blind ? 'orient.acc_rough' : 'orient.acc_approx'
  return <span class={cls}>{tr(key)}</span>
}

/** Честные подписи под стрелкой: устарело / «пройдите шаги». */
function OrientNotes({ ctx }: { ctx: OrientContext }) {
  const sensors = orientSensors.value
  const walkHint = ctx.phraseKey === null || sensors === 'denied' || sensors === 'unsupported'
  return (
    <>
      {orientStale.value && <p class="orient-note orient-note-warn">{tr('orient.stale')}</p>}
      {walkHint && <p class="orient-note">{tr('orient.walk_hint')}</p>}
    </>
  )
}

/**
 * Выбор способа движения (спека 13): «карта и линия» или «AR-ведение».
 * Тап по AR — тот же жест даёт iOS-разрешения (датчики + камера) и
 * предзагружает AR-чанк; выбор живёт на устройстве.
 */
function GuidanceChoice() {
  const pref = guidancePref.value
  const pick = (next: GuidancePref): void => {
    setGuidancePref(next)
    if (next !== 'ar') {
      dropWarmup()
      return
    }
    void warmupAr(true)
    void importChunk(() => import('../ar'))
  }
  return (
    <div class="guide-row">
      <button
        type="button"
        class={pref === 'map' ? 'guide-btn guide-on' : 'guide-btn'}
        onClick={() => pick('map')}
      >
        <Glyph svg={GLYPH_MAP} />
        {tr('ar.choice_map')}
      </button>
      <button
        type="button"
        class={pref === 'ar' ? 'guide-btn guide-on' : 'guide-btn'}
        onClick={() => pick('ar')}
      >
        <Glyph svg={GLYPH_AR} />
        {tr('ar.choice_ar')}
        <span class="guide-new">{tr('ar.new')}</span>
      </button>
    </div>
  )
}

/** Экран ОРИЕНТАЦИЯ (спека 05): куда двигаться до старта, GPS ещё слеп. */
export function StartArrow() {
  langTick.value
  const v = venue.value
  const plan = routePlan.value
  if (!v || !plan) return null
  const ctx = orientContext(v, plan)
  const world = orientHeading.value
  const bound = world !== null
  const title = targetId.value ? featureName(v, targetId.value) : tr('follow.back_route')
  const angle = resolveAngle(ctx, world)
  const canBind = ctx.anchorHeadingDeg !== null && orientSensors.value === 'idle' && !bound

  return (
    <section class="orient-screen">
      <div class="orient-mesh" aria-hidden="true" />
      <header class="orient-head">
        <button type="button" class="lang-btn" aria-label={tr('common.back')} onClick={exitOrient}>
          ←
        </button>
        <div class="orient-head-text">
          <div class="orient-kicker">{tr('orient.step_one')}</div>
          <div class="orient-headline">{tr('orient.first_step_q')}</div>
        </div>
        {/* Пустышка уравновешивает кнопку «назад»: заголовок ровно по центру. */}
        <span class="orient-head-spacer" />
      </header>

      <div class="orient-body">
        <Compass angleDeg={angle} live={bound} />
        <div class="orient-phrase-box">
          {ctx.phraseKey && <div class="orient-phrase">{tr(ctx.phraseKey)}</div>}
          {ctx.firstLegM !== null && (
            <div class="orient-leg">
              {tr('orient.first_leg', { dist: formatDistance(ctx.firstLegM) })}
            </div>
          )}
          <div class="orient-target">{title}</div>
        </div>
        <div class="orient-honesty">
          <AccuracyBadge bound={bound} />
          {canBind && (
            <button type="button" class="orient-bind glass" onClick={() => void bindArrow()}>
              {tr('orient.bind')}
            </button>
          )}
          <OrientNotes ctx={ctx} />
        </div>
      </div>

      <div class="orient-foot">
        <GuidanceChoice />
        <button type="button" class="cta-hero cta-hero-wide light-sweep" onClick={confirmGo}>
          <span class="cta-hero-label">{tr('orient.go')}</span>
        </button>
      </div>
    </section>
  )
}
